import { useState, useEffect, useCallback, useRef } from "react";
import AdminLayout from "../components/AdminLayout";
import {
  Card, Button, Modal, Input, Select, EmptyState, SearchBar, Pagination, FilterBar,
  PageHeader, FilterSelect, ListSkeleton, Tabs, MonthPicker, StatusBadge, IconButton,
  Avatar, StickyActions, cx,
  ListPage, ListSurface, stickyHeadCls, actionColCls, actionHeadCls,
  listTableCls, listCardsCls, thCls, tdCls, actionW,
} from "../components/UI";
import api from "../utils/api";
import toast from "react-hot-toast";
import { Plus, Pencil, Trash2, Download, Clock, MapPin, ChartLine } from "lucide-react";

const LIMIT = 20;
const STATUS_OPTIONS = ["present", "absent", "half-day", "leave"];

const fmtDay = (d) =>
  d ? new Date(d + "T00:00:00").toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" }) : "—";

function RecordCard({ r, onEdit, onDelete }) {
  return (
    <Card className="p-4" hover>
      {/* actions lead the card, mirroring the table's first column */}
      <div className="flex items-center gap-1.5 pb-3 mb-3 border-b border-ink-100">
        <Button size="xs" variant="outline" icon={<Pencil size={13} />} onClick={() => onEdit(r)}>Edit</Button>
        <Button size="xs" variant="dangerSoft" icon={<Trash2 size={13} />} onClick={() => onDelete(r._id)}>Delete</Button>
      </div>

      <div className="flex items-start gap-3">
        <Avatar name={r.crewId?.name || "?"} tone="brand" size="md" />
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-ink-900 truncate">{r.crewId?.name || "—"}</div>
          <div className="text-xs text-ink-400 truncate mt-0.5">{fmtDay(r.date)}{r.crewId?.role ? ` · ${r.crewId.role}` : ""}</div>
        </div>
        <StatusBadge status={r.status} />
      </div>

      {r.siteId?.name && (
        <div className="flex items-center gap-1.5 mt-3 text-xs text-ink-500">
          <MapPin size={12} className="text-ink-400 shrink-0" /><span className="truncate">{r.siteId.name}</span>
        </div>
      )}

      <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-ink-100 text-center">
        {[["In", r.checkIn], ["Out", r.checkOut], ["Hours", r.workHours ? `${r.workHours.toFixed(1)}h` : null]].map(([label, val]) => (
          <div key={label}>
            <div className="text-[10px] font-semibold text-ink-400 uppercase tracking-wider">{label}</div>
            <div className={cx("text-sm font-semibold mt-0.5 tnum", label === "Hours" ? "text-brand-600" : "text-ink-800 font-mono")}>
              {val || <span className="text-ink-300">—</span>}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

export default function AdminAttendance() {
  const today = new Date().toISOString().split("T")[0];
  const currentMonth = today.slice(0, 7);

  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState([]);
  const [crews, setCrews] = useState([]);
  const [sites, setSites] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("records");
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ month: currentMonth, crewId: "", siteId: "", status: "" });
  const [modal, setModal] = useState(null);
  const [editRec, setEditRec] = useState(null);
  const [form, setForm] = useState({ crewId: "", siteId: "", date: today, checkIn: "09:00", checkOut: "", status: "present", notes: "" });
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const debRef = useRef(null);

  const fetchRecords = useCallback(async () => {
    if (activeTab !== "records") return;
    setLoading(true);
    try {
      const p = new URLSearchParams({ page, limit: LIMIT });
      if (filters.month) p.set("month", filters.month);
      if (filters.crewId) p.set("crewId", filters.crewId);
      if (filters.siteId) p.set("siteId", filters.siteId);
      if (filters.status) p.set("status", filters.status);
      if (search) p.set("search", search);
      const { data } = await api.get(`/attendance/admin?${p}`);
      setRecords(data.data.records || []); setTotal(data.data.total); setPages(data.data.pages);
    } catch { toast.error("Failed to load attendance"); }
    finally { setLoading(false); }
  }, [page, filters, search, activeTab]);

  const fetchSummary = useCallback(async () => {
    if (activeTab !== "summary") return;
    setLoading(true);
    try {
      const p = new URLSearchParams({ month: filters.month });
      if (filters.siteId) p.set("siteId", filters.siteId);
      if (filters.crewId) p.set("crewId", filters.crewId);
      const { data } = await api.get(`/attendance/admin/monthly-summary?${p}`);
      setSummary(data.data || []);
    } catch { toast.error("Failed to load summary"); }
    finally { setLoading(false); }
  }, [filters.month, filters.siteId, filters.crewId, activeTab]);

  useEffect(() => {
    Promise.all([api.get("/crews?limit=200"), api.get("/sites/admin?limit=200")])
      .then(([c, s]) => { setCrews(c.data.data.crews || c.data.data || []); setSites(s.data.data.sites || s.data.data || []); })
      .catch(() => { });
  }, []);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);
  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const handleSearch = (v) => {
    clearTimeout(debRef.current);
    debRef.current = setTimeout(() => { setSearch(v); setPage(1); }, 300);
    setSearch(v);
  };
  const setF = (k, v) => setFilters(p => ({ ...p, [k]: v }));

  const openCreate = () => {
    setForm({ crewId: "", siteId: "", date: today, checkIn: "09:00", checkOut: "", status: "present", notes: "" });
    setEditRec(null); setModal("form");
  };
  const openEdit = (r) => {
    setEditRec(r);
    setForm({
      crewId: r.crewId?._id || r.crewId || "", siteId: r.siteId?._id || r.siteId || "",
      date: r.date, checkIn: r.checkIn || "", checkOut: r.checkOut || "", status: r.status, notes: r.notes || "",
    });
    setModal("form");
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (!editRec) { await api.post("/attendance/admin", form); toast.success("Record created!"); }
      else {
        await api.put(`/attendance/admin/${editRec._id}`,
          { checkIn: form.checkIn, checkOut: form.checkOut, status: form.status, notes: form.notes });
        toast.success("Updated!");
      }
      setModal(null); fetchRecords();
    } catch (err) { toast.error(err.response?.data?.message || "Failed"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    try { await api.delete(`/attendance/admin/${id}`); toast.success("Deleted"); fetchRecords(); }
    catch { toast.error("Failed"); }
  };

  /* Fetched through the api client rather than window.open: the export is behind
     adminOnly, and a plain window.open sends no Authorization header — it also
     resolved against the app's own origin instead of the API host, so it never
     reached the server at all. */
  const exportCSV = async () => {
    setExporting(true);
    try {
      const p = new URLSearchParams({ month: filters.month });
      if (filters.crewId) p.set("crewId", filters.crewId);
      if (filters.siteId) p.set("siteId", filters.siteId);
      if (filters.status) p.set("status", filters.status);

      const { data } = await api.get(`/attendance/admin/export-csv?${p}`, { responseType: "blob" });

      const url = URL.createObjectURL(new Blob([data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `attendance-${filters.month || "all"}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <AdminLayout fullHeight>
      <ListPage>
        <PageHeader
          title="Attendance"
          subtitle={`${total} record${total !== 1 ? "s" : ""} this month`}
          action={
            <div className="flex gap-2 w-full sm:w-auto">
              <Button variant="secondary" onClick={exportCSV} loading={exporting} icon={<Download size={15} />} className="flex-1 sm:flex-none">Export</Button>
              <Button onClick={openCreate} icon={<Plus size={16} />} className="flex-1 sm:flex-none">Add Record</Button>
            </div>
          }
        />

        <div className="shrink-0">
          <Tabs
            active={activeTab}
            onChange={v => { setActiveTab(v); setPage(1); }}
            tabs={[
              { key: "records", label: "Records", icon: <Clock size={14} /> },
              { key: "summary", label: "Monthly Summary", icon: <ChartLine size={14} /> },
            ]}
          />
        </div>

        <FilterBar className="shrink-0">
          {activeTab === "records" && (
            <SearchBar value={search} onChange={handleSearch} placeholder="Search crew name, role…" className="flex-1 min-w-[160px]" />
          )}
          <MonthPicker value={filters.month} onChange={v => { setF("month", v); setPage(1); }} className="min-w-[140px]" />
          <FilterSelect value={filters.crewId} onChange={v => { setF("crewId", v); setPage(1); }} ariaLabel="Filter by crew" className="min-w-[140px]">
            <option value="">All Crew</option>
            {crews.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
          </FilterSelect>
          <FilterSelect value={filters.siteId} onChange={v => { setF("siteId", v); setPage(1); }} ariaLabel="Filter by site" className="min-w-[140px]">
            <option value="">All Sites</option>
            {sites.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
          </FilterSelect>
          {activeTab === "records" && (
            <FilterSelect value={filters.status} onChange={v => { setF("status", v); setPage(1); }} ariaLabel="Filter by status" className="min-w-[130px]">
              <option value="">All Status</option>
              {STATUS_OPTIONS.map(s => <option key={s} value={s} className="capitalize">{s}</option>)}
            </FilterSelect>
          )}
        </FilterBar>

        {/* ══ Records ══ */}
        {activeTab === "records" && (
          loading ? <div className="flex-1 min-h-0 overflow-hidden"><ListSkeleton rows={8} cols={7} /></div>
            : records.length === 0 ? (
              <Card className="flex-1 min-h-0 grid place-items-center">
                <EmptyState icon={<Clock size={22} />} title="No records"
                  description="No attendance records match these filters."
                  action={<Button onClick={openCreate} icon={<Plus size={16} />}>Add Record</Button>} />
              </Card>
            ) : (
              <ListSurface footer={<Pagination page={page} pages={pages} total={total} limit={LIMIT} onPage={setPage} />}>
                <table className={listTableCls}>
                  <thead className={stickyHeadCls}>
                    <tr>
                      <th className={cx(thCls, actionHeadCls, actionW(2))}>Actions</th>
                      <th className={cx(thCls, "w-[130px]")}>Date</th>
                      <th className={thCls}>Crew</th>
                      <th className={thCls}>Site</th>
                      <th className={cx(thCls, "w-[104px]")}>Check In</th>
                      <th className={cx(thCls, "w-[104px]")}>Check Out</th>
                      <th className={cx(thCls, "w-[88px]")}>Hours</th>
                      <th className={cx(thCls, "w-[118px]")}>Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ink-100">
                    {records.map(r => (
                      <tr key={r._id} className="group hover:bg-ink-50 transition-colors">
                        <td className={cx(tdCls, actionColCls, "px-3")}>
                          <div className="flex gap-0.5">
                            <IconButton size="sm" variant="outline" icon={<Pencil size={15} />} onClick={() => openEdit(r)} title="Edit" />
                            <IconButton size="sm" variant="danger" icon={<Trash2 size={15} />} onClick={() => handleDelete(r._id)} title="Delete" />
                          </div>
                        </td>
                        <td className={cx(tdCls, "text-ink-700 font-medium text-xs")}>
                          <div className="truncate">{fmtDay(r.date)}</div>
                        </td>
                        <td className={tdCls}>
                          <div className="flex items-center gap-2.5 min-w-0">
                            <Avatar name={r.crewId?.name || "?"} tone="brand" size="xs" />
                            <div className="min-w-0">
                              <div className="font-medium text-ink-800 truncate">{r.crewId?.name || "—"}</div>
                              {r.crewId?.role && <div className="text-xs text-ink-400 truncate">{r.crewId.role}</div>}
                            </div>
                          </div>
                        </td>
                        <td className={cx(tdCls, "text-xs text-ink-500")}>
                          <div className="truncate">{r.siteId?.name || <span className="text-ink-300">—</span>}</div>
                        </td>
                        <td className={cx(tdCls, "font-mono text-sm text-ink-700 tnum")}>{r.checkIn || <span className="text-ink-300">—</span>}</td>
                        <td className={cx(tdCls, "font-mono text-sm text-ink-700 tnum")}>{r.checkOut || <span className="text-ink-300">—</span>}</td>
                        <td className={cx(tdCls, "text-sm")}>
                          {r.workHours ? <span className="font-semibold text-brand-600 tnum">{r.workHours.toFixed(1)}h</span> : <span className="text-ink-300">—</span>}
                        </td>
                        <td className={tdCls}><StatusBadge status={r.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className={listCardsCls}>
                  {records.map(r => <RecordCard key={r._id} r={r} onEdit={openEdit} onDelete={handleDelete} />)}
                </div>
              </ListSurface>
            )
        )}

        {/* ══ Monthly summary ══ */}
        {activeTab === "summary" && (
          loading ? <div className="flex-1 min-h-0 overflow-hidden"><ListSkeleton rows={5} cols={7} /></div>
            : summary.length === 0 ? (
              <Card className="flex-1 min-h-0 grid place-items-center">
                <EmptyState icon={<ChartLine size={22} />} title="No summary data" description={`No attendance recorded for ${filters.month}.`} />
              </Card>
            ) : (
              <ListSurface>
                <table className={listTableCls}>
                  <thead className={stickyHeadCls}>
                    <tr>
                      <th className={thCls}>Crew Member</th>
                      <th className={thCls}>Role</th>
                      <th className={cx(thCls, "w-[132px]")}>Last Logged</th>
                      <th className={cx(thCls, "w-[92px]")}>Present</th>
                      <th className={cx(thCls, "w-[92px]")}>Half Day</th>
                      <th className={cx(thCls, "w-[92px]")}>Leave</th>
                      <th className={cx(thCls, "w-[92px]")}>Absent</th>
                      <th className={cx(thCls, "w-[116px]")}>Total Hours</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ink-100">
                    {summary.map((s, i) => (
                      <tr key={s.crew?._id || i} className={cx("hover:bg-ink-50 transition-colors", !s.daysLogged && "opacity-60")}>
                        <td className={tdCls}>
                          <div className="flex items-center gap-2.5 min-w-0">
                            <Avatar name={s.crew?.name || "?"} tone={s.crew?.inactive ? "slate" : "brand"} size="sm" />
                            <div className="min-w-0">
                              <div className="font-semibold text-ink-800 truncate">{s.crew?.name || "—"}</div>
                              {s.crew?.employeeId && <div className="text-[11px] text-ink-400 font-mono truncate">#{s.crew.employeeId}</div>}
                            </div>
                          </div>
                        </td>
                        <td className={cx(tdCls, "text-ink-500 text-xs")}>
                          <div className="truncate">{s.crew?.role || "—"}</div>
                        </td>
                        <td className={cx(tdCls, "text-xs")}>
                          {s.lastDate
                            ? <>
                                <div className="text-ink-700 truncate">{fmtDay(s.lastDate)}</div>
                                <div className="text-ink-400 truncate">{s.daysLogged} day{s.daysLogged !== 1 ? "s" : ""} logged</div>
                              </>
                            : <span className="text-ink-300">Never logged</span>}
                        </td>
                        <td className={tdCls}><span className="font-semibold text-emerald-600 tnum">{s.present}</span></td>
                        <td className={tdCls}><span className="font-semibold text-amber-600 tnum">{s.halfDay}</span></td>
                        <td className={tdCls}><span className="font-semibold text-brand-600 tnum">{s.leave}</span></td>
                        <td className={tdCls}><span className="font-semibold text-red-600 tnum">{s.absent}</span></td>
                        <td className={tdCls}><span className="font-bold text-ink-900 tnum">{(s.totalHours || 0).toFixed(1)}h</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className={listCardsCls}>
                  {summary.map((s, i) => (
                    <Card key={s.crew?._id || i} className={cx("p-4", !s.daysLogged && "opacity-60")}>
                      <div className="flex items-center gap-3">
                        <Avatar name={s.crew?.name || "?"} tone={s.crew?.inactive ? "slate" : "brand"} size="md" />
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-ink-900 truncate">{s.crew?.name || "—"}</div>
                          <div className="text-xs text-ink-400 truncate">
                            {s.lastDate ? `${s.daysLogged} day${s.daysLogged !== 1 ? "s" : ""} · last ${fmtDay(s.lastDate)}` : "Never logged"}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-lg font-bold text-ink-900 font-display tnum leading-none">{(s.totalHours || 0).toFixed(1)}h</div>
                          <div className="text-[10px] text-ink-400 mt-1">total</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-4 gap-2 mt-3 pt-3 border-t border-ink-100 text-center">
                        {[["Present", s.present, "text-emerald-600"], ["Half", s.halfDay, "text-amber-600"],
                          ["Leave", s.leave, "text-brand-600"], ["Absent", s.absent, "text-red-600"]].map(([l, v, c]) => (
                          <div key={l}>
                            <div className={cx("text-base font-bold tnum", c)}>{v}</div>
                            <div className="text-[10px] text-ink-400 uppercase tracking-wider mt-0.5">{l}</div>
                          </div>
                        ))}
                      </div>
                    </Card>
                  ))}
                </div>
              </ListSurface>
            )
        )}
      </ListPage>

      <Modal open={modal === "form"} onClose={() => setModal(null)}
        title={editRec ? "Edit Record" : "Add Attendance"}
        subtitle={editRec ? `${editRec.crewId?.name || ""} · ${fmtDay(editRec.date)}` : "Log a day for a crew member"}
        icon={<Clock size={18} />}>
        <form onSubmit={handleSubmit} className="space-y-4">
          {!editRec && (
            <>
              <Select label="Crew Member" required value={form.crewId} onChange={e => setForm({ ...form, crewId: e.target.value })}>
                <option value="">Select crew member</option>
                {crews.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
              </Select>
              <Input label="Date" type="date" required max={today} value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} />
            </>
          )}
          <Select label="Site" hint="Optional" value={form.siteId} onChange={e => setForm({ ...form, siteId: e.target.value })}>
            <option value="">— No Site —</option>
            {sites.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
          </Select>
          <div className="grid grid-cols-2 gap-4">
            <Input label="Check In" type="time" value={form.checkIn} onChange={e => setForm({ ...form, checkIn: e.target.value })} />
            <Input label="Check Out" type="time" value={form.checkOut} onChange={e => setForm({ ...form, checkOut: e.target.value })} />
          </div>
          <Select label="Status" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
            {STATUS_OPTIONS.map(s => <option key={s} value={s} className="capitalize">{s}</option>)}
          </Select>
          <Input label="Notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Optional notes" />

          <StickyActions>
            <Button type="button" variant="secondary" onClick={() => setModal(null)} block className="sm:w-auto">Cancel</Button>
            <Button type="submit" loading={saving} block className="sm:w-auto">{editRec ? "Update" : "Add Record"}</Button>
          </StickyActions>
        </form>
      </Modal>
    </AdminLayout>
  );
}
